// FilterEdit/index.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './FilterEdit.module.css';
import TuiEditor from '../../../components/common/TuiEditor';
import axios from 'axios';
import { API_BASE_URL } from '../../../apiConfig';
import { storage } from '../../../firebase'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

export default function FilterEdit() {
  const { newArrivalFilterId } = useParams();
  const navigate = useNavigate();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialContent, setInitialContent] = useState('');
  const [initialTitle, setInitialTitle] = useState('');
  const [link, setLink] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    setLoading(true);
    setError(null);

    // API로 데이터 가져오기
    axios.get(`${API_BASE_URL}/api/v1/admin/new-filter/${newArrivalFilterId}`)
      .then((response) => {
        const data = response.data.data;
        if (!data) {
          throw new Error('필터 데이터를 불러올 수 없습니다.');
        }
        setInitialTitle(data.productName || '');
        setLink(data.link || '');
        setInitialContent(data.content || '');
        setThumbnailPreview(data.image || '');
        setOriginalImageUrl(data.image || '');
      })
      .catch((err) => {
        setError('필터 정보를 불러오는 중 오류가 발생했습니다.');
        console.error('필터 데이터 로딩 오류:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [newArrivalFilterId]);

  // 썸네일 변경
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current.click();
  };

  // URL 유효성 검사
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Firebase에 이미지 업로드 및 URL 가져오기
  const uploadImageToFirebase = async (file) => {
    if (!file) return null;
    const uniqueFileName = `${uuidv4()}_${file.name}`; // 고유한 파일명 생성
    const storageRef = ref(storage, `filter-images/${uniqueFileName}`); // Firebase Storage 참조 생성
    
    try {
      const snapshot = await uploadBytes(storageRef, file); // 파일 업로드
      const url = await getDownloadURL(snapshot.ref); // 다운로드 URL 가져오기
      console.log('이미지 업로드 성공, URL:', url);
      return url;
    } catch (error) {
      console.error('Firebase 이미지 업로드 실패:', error);
      throw new Error('이미지 업로드 실패');
    }
  };

  // 필터 수정 핸들러 (TuiEditor에서 제목과 내용을 받음)
  const handleFilterSubmit = async (editorContent, editorTitle) => {
    // 제목은 TuiEditor에서 처리하므로 editorTitle 사용
    if (!editorTitle || !editorTitle.trim()) {
      alert('제목을 입력해주세요.');
      return Promise.reject(new Error('제목이 필요합니다.'));
    }

    // 링크 검증 (선택사항이지만 입력했다면 유효한 URL이어야 함)
    if (link.trim() && !isValidUrl(link.trim())) {
      alert('올바른 URL 형식으로 입력해주세요. (예: https://example.com)');
      return Promise.reject(new Error('유효하지 않은 URL입니다.'));
    }

    setIsSubmitting(true);
    try {
      let imageUrl = originalImageUrl; // 기본값은 기존 이미지 URL

      // 새로운 썸네일 파일이 선택되었으면 Firebase에 업로드
      if (thumbnailFile) {
        imageUrl = await uploadImageToFirebase(thumbnailFile);
      }

      const filterData = {
        productName: editorTitle.trim(),  // TuiEditor에서 받은 제목 사용
        content: editorContent,
        image: imageUrl,
        // image: thumbnailFile || thumbnailPreview, // 새 파일이 있으면 사용, 없으면 기존 이미지
        link: link.trim() || null
      };

      console.log('수정된 필터 데이터:', filterData);
      
      // 실제로는 여기서 API 호출
      const response = await axios.put(
        `${API_BASE_URL}/api/v1/admin/new-filter/${newArrivalFilterId}`, 
        filterData,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.data.code !== 1) {
        throw new Error('필터 수정에 실패했습니다.')
      }
      
      alert('필터가 성공적으로 수정되었습니다.');
      navigate(`/filters/${newArrivalFilterId}`);
      return true;
    } catch (error) {
      console.error('필터 수정 오류:', error);
      alert('오류가 발생했습니다. 다시 시도해주세요.');
      return Promise.reject(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.loading}>로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <div className={styles.error}>{error}</div>
          <button onClick={() => navigate(`/filters/${newArrivalFilterId}`)} className={styles.backButton}>
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formSection}>
        {/* 링크 입력 */}
        <div className={styles.linkSection}>
          <label htmlFor="purchaseLink" className={styles.label}>
            구매 링크
          </label>
          <input
            id="purchaseLink"
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="구매 페이지 URL을 입력해주세요"
            className={styles.linkInputField}
          />
          <p className={styles.inputHelp}>
            제품을 구매할 수 있는 페이지 링크를 입력하세요. (선택사항)
          </p>
        </div>

        {/* 썸네일 섹션 */}
        <div className={styles.thumbnailSection}>
          <label className={styles.label}>
            썸네일 이미지 <span className={styles.required}>*</span>
          </label>
          <div className={styles.thumbnailContent}>
            <div className={styles.thumbnailPreview}>
              <img 
                src={thumbnailPreview} 
                alt="썸네일 미리보기" 
                className={styles.previewImage} 
              />
            </div>
            <div className={styles.thumbnailControls}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleThumbnailChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className={styles.fileSelectButton} 
                onClick={handleSelectFile}
              >
                썸네일 이미지 수정
              </button>
              <p className={styles.thumbnailHelp}>
                권장 크기: 300 X 300 픽셀, 최대 2MB (PNG, JPG, JPEG)
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.editorSection}>
        <TuiEditor
          initialTitle={initialTitle}
          initialContent={initialContent}
          onSubmit={handleFilterSubmit}
          isSubmitting={isSubmitting}
          submitButtonText="수정하기"
        />
      </div>
    </div>
  );
}